import { PlaceRef } from "@/types/entity"
import { Itinerary } from "@/types/itinerary"
import { Place, PlacePlaceholder, PlaceSummary } from "@/types/place"
import { TripRef } from "@/types/trip"
import {
  CaseReducer,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"

import { isTravelerEntry } from "@/lib/itinerary-utils"
import { assertExists } from "@/lib/utils/assertions"
import { getConstantEmptySet } from "@/lib/utils/collections"
import { sameDay } from "../date-utils"
import {
  computeItineraryDays,
  filterScheduledEvents,
  filterUnscheduledEvents,
  isAutoGenerated,
  isCancelledBooking,
  isPendingFlightBookingEvent,
  isPlaceItineraryEvent,
  startDay,
} from "../itinerary-utils"
import { itineraryApi } from "./api/itinerary-api"
import { RootState } from "./redux-store"
import {
  selectTripDraftStartDate,
  selectTripDuration,
  selectTripRef,
  selectTripSavedPlaces,
  selectTripTimeZone,
} from "./trip-slice"
import { selectUserTimeZone } from "./user-slice"

export const EMPTY_ITINERARY: Itinerary = Object.freeze({
  events: [],
  day_titles: [],
  day_notes: [],
})

function selectItineraryForTripRef(
  state: RootState,
  tripRef: TripRef | null
): Itinerary {
  if (!tripRef) {
    return EMPTY_ITINERARY
  }
  return (
    itineraryApi.endpoints.getItinerary.select({ tripRef })(state).data ??
    EMPTY_ITINERARY
  )
}

export interface ItinerarySliceState {
  timeZone: string | undefined
  enableLocalTime: boolean
  showRoute: boolean
  unschedulingEvents: boolean
  removingIdeas: boolean
  dayOperationPending: boolean
}

export const itinerarySliceInitialState: ItinerarySliceState = {
  timeZone: undefined,
  enableLocalTime: false,
  showRoute: false,
  unschedulingEvents: false,
  removingIdeas: false,
  dayOperationPending: false,
}

const selectSliceState = (state: RootState) => state.itinerarySlice

export const selectItinerary = (state: RootState): Itinerary =>
  selectItineraryForTripRef(state, selectTripRef(state))

export const selectAllPersistentEvents = createSelector(
  [selectItinerary],
  (itinerary) => {
    return itinerary.events
  }
)

export const selectAllPersistentEventIds = createSelector(
  [selectItinerary],
  (itinerary) => {
    return itinerary.events.map((event) => event.id)
  }
)

export const selectCommentedEventCount = createSelector(
  [selectAllPersistentEvents],
  (events) => events.filter((event) => event.comment_count > 0).length
)

export const selectHasItineraryEvents = createSelector(
  [selectAllPersistentEvents],
  (events) => events.length > 0
)

export const selectHasManualHotelItineraryEvents = createSelector(
  [selectAllPersistentEvents],
  (events) =>
    events.some(
      (e) => e.type === "hotel" && isTravelerEntry(e) && e.scheduled === true
    )
)

export const selectScheduledEvents = createSelector(
  [selectAllPersistentEvents],
  (allEvents) => {
    return filterScheduledEvents(allEvents)
  }
)

export const selectUnscheduledEvents = createSelector(
  [selectAllPersistentEvents],
  (allEvents) => {
    return filterUnscheduledEvents(allEvents)
  }
)

export const selectHasUnscheduledPlaceEvents = createSelector(
  [selectUnscheduledEvents],
  (events) => events.filter(isPlaceItineraryEvent).length > 0
)

export const selectUnscheduledEventCount = createSelector(
  [selectUnscheduledEvents],
  (events) => events.length
)

export const selectHasUnscheduledEvents = createSelector(
  [selectUnscheduledEventCount],
  (count) => count > 0
)

export const selectDayTitles = createSelector(
  [selectItinerary],
  (itinerary) => itinerary.day_titles
)

export const selectDayNotes = createSelector(
  [selectItinerary],
  (itinerary) => itinerary.day_notes
)

export const selectGeneratedEvents = createSelector(
  [selectAllPersistentEvents],
  (events) => {
    return events.filter(isAutoGenerated)
  }
)

export const selectHasGeneratedEvents = createSelector(
  [selectGeneratedEvents],
  (events) => events.length > 0
)

export const selectHasNonGeneratedEvents = createSelector(
  [selectAllPersistentEvents],
  (events) => events.some((e) => !isAutoGenerated(e))
)

export const selectEventsWithPlaces = createSelector(
  [selectAllPersistentEvents],
  (events) => {
    return events.filter(isPlaceItineraryEvent)
  }
)

export const selectEventsWithoutPlaces = createSelector(
  [selectAllPersistentEvents],
  (events) => {
    return events.filter((e) => !isPlaceItineraryEvent(e))
  }
)

export const selectAddedPlaces = createSelector(
  [selectItinerary],
  (itinerary) =>
    itinerary.events.filter(isPlaceItineraryEvent).map((event) => event.place)
)

export const selectBookedEvents = createSelector(
  selectAllPersistentEvents,
  (events) =>
    events
      .filter((e) => e.status === "booked" || isPendingFlightBookingEvent(e))
      .sort((a, b) => {
        return a.start_at.epoch_millis - b.start_at.epoch_millis
      })
)

export const selectBookedAndCancelledEvents = createSelector(
  selectAllPersistentEvents,
  (events) =>
    events
      .filter(
        (e) =>
          e.status === "booked" ||
          isCancelledBooking(e) ||
          isPendingFlightBookingEvent(e)
      )
      .sort((a, b) => {
        return a.start_at.epoch_millis - b.start_at.epoch_millis
      })
)

export const selectBookedEventCount = createSelector(
  [selectBookedEvents],
  (events) => events.length
)

export const selectEventCount = createSelector(
  [selectAllPersistentEvents],
  (events) => events.length
)

export const selectHasAnyEvents = createSelector(
  [selectEventCount],
  (eventCount) => eventCount > 0
)

export const selectHasScheduledEvents = createSelector(
  [selectScheduledEvents],
  (events) => events.length > 0
)

export const selectScheduledEventCount = createSelector(
  [selectScheduledEvents],
  (events) => events.length
)

export const selectAllPlaceRefsOnItinerary = createSelector(
  [selectAllPersistentEvents],
  (events) => {
    return events.length === 0
      ? getConstantEmptySet<PlaceRef>()
      : new Set<PlaceRef>(
          events.filter(isPlaceItineraryEvent).map((e) => e.place.entity_ref)
        )
  }
)

export const selectIsPlaceOnItinerary = createSelector(
  [
    selectAllPlaceRefsOnItinerary,
    (_state, place: Place | PlacePlaceholder) => place,
  ],
  (placeRefs, place) => placeRefs.has(place.entity_ref)
)

export const selectPlaceRefToEventIdMap = createSelector(
  [selectAllPersistentEvents],
  (events) => {
    if (events.length === 0) {
      return new Map<PlaceRef, number>()
    }

    const map = new Map<PlaceRef, number>()
    events.forEach((event) => {
      if (isPlaceItineraryEvent(event)) {
        map.set(event.place.entity_ref, event.id)
      }
    })
    return map
  }
)

export const selectCalendarTimeZone = createSelector(
  [selectSliceState, selectTripTimeZone],
  (state, tripTimeZone) => {
    return state.timeZone ?? tripTimeZone
  }
)

export const selectAllItineraryTimeZones = createSelector(
  [selectAllPersistentEvents, selectTripTimeZone],
  (itineraryEvents, tripTimeZone) => {
    const timeZones = itineraryEvents.map((e) => e.time_zone)
    timeZones.push(tripTimeZone) // Make sure the trip timezone is there too (even no events in that timezone)
    return [...new Set(timeZones)].sort()
  }
)

// place list with both saved and added to trip (saved or not)
export const selectAddedAndSavedPlaces = createSelector(
  [selectTripSavedPlaces, selectEventsWithPlaces],
  (savedPlaces, eventsWithPlaces) => {
    const eventPlaces = eventsWithPlaces.map((e) => e.place)
    const uniquePlaces = new Map()
    ;[...savedPlaces, ...eventPlaces].forEach((place) => {
      if (!uniquePlaces.has(place.entity_ref)) {
        uniquePlaces.set(place.entity_ref, place)
      }
    })

    return Array.from<PlaceSummary>(uniquePlaces.values())
  }
)

export const selectItineraryDays = createSelector(
  [
    selectUserTimeZone,
    selectTripDraftStartDate,
    selectTripDuration,
    selectScheduledEvents,
    selectDayTitles,
    selectDayNotes,
  ],
  (
    userTimeZone,
    draftStartDate,
    draftDuration,
    events,
    dayTitles,
    dayNotes
  ) => {
    return computeItineraryDays(
      userTimeZone,
      draftStartDate,
      draftDuration,
      events,
      dayTitles,
      dayNotes
    )
  }
)

export const selectNormalItineraryDays = createSelector(
  [selectItineraryDays],
  (days) => days.filter((day) => day.type === "normal")
)

export const selectItineraryDayCount = createSelector(
  [selectNormalItineraryDays],
  (days) => days.length
)

export const selectEventsStartingOnDayNumber = createSelector(
  [
    selectScheduledEvents,
    selectNormalItineraryDays,
    (_state, dayNumber: number) => dayNumber,
  ],
  (events, itineraryDays, dayNumber) => {
    const itineraryDay = itineraryDays.find(
      (day) => day.type === "normal" && day.dayNumber === dayNumber
    )
    const dayObject = assertExists(
      itineraryDay,
      `Day number ${dayNumber} not found in itinerary`
    ).day
    return events.filter((event) => sameDay(startDay(event), dayObject))
  }
)

export const selectCalendarLocalTimeEnable = createSelector(
  [selectSliceState],
  (state) => {
    return state.enableLocalTime
  }
)

export const selectShowRoute = createSelector(
  [selectSliceState],
  (state) => state.showRoute
)

export const selectUnschedulingEvents = createSelector(
  [selectSliceState],
  (state) => state.unschedulingEvents
)

export const selectRemovingIdeas = createSelector(
  [selectSliceState],
  (state) => state.removingIdeas
)

export const selectDayOperationPending = createSelector(
  [selectSliceState],
  (state) => state.dayOperationPending
)

const setCalendarTimeZone: CaseReducer<
  ItinerarySliceState,
  PayloadAction<string | undefined>
> = (state, action) => {
  state.timeZone = action.payload
}

const setCalendarLocalTimeEnable: CaseReducer<
  ItinerarySliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.enableLocalTime = action.payload
}

const setShowRoute: CaseReducer<ItinerarySliceState, PayloadAction<boolean>> = (
  state,
  action
) => {
  state.showRoute = action.payload
}

const setUnschedulingEvents: CaseReducer<
  ItinerarySliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.unschedulingEvents = action.payload
}

const setRemovingIdeas: CaseReducer<
  ItinerarySliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.removingIdeas = action.payload
}

const setDayOperationPending: CaseReducer<
  ItinerarySliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.dayOperationPending = action.payload
}

const itinerarySlice = createSlice({
  name: "itinerary",
  initialState: itinerarySliceInitialState,
  reducers: {
    setCalendarTimeZone,
    setCalendarLocalTimeEnable,
    setShowRoute,
    setUnschedulingEvents,
    setRemovingIdeas,
    setDayOperationPending,
  },
})

export type ItinerarySlice = typeof itinerarySlice

export default itinerarySlice
